import { FileReference, TrafficRulesCatalog, UxOnly } from '@thive/fddm-types';

export type CatalogActions = {
  trafficRules: {
    set(trafficRules: TrafficRulesCatalog): void;
    vehicleModes: {
      set(vehicleModes: TrafficRulesCatalog['vehicleModes']): void;
    };
    maxSpeeds: {
      set(maxSpeeds: TrafficRulesCatalog['maxSpeeds']): void;
    };
  };

  getFileReferences(): FileReference[];
  addFileReference(fileReference: FileReference): void;
  removeFileReference(fileReference: FileReference): void;
};

type CatalogSliceState = {
  uxonly: UxOnly;
};

type CatalogAction = string | { type: string };

type CatalogSet<TState extends CatalogSliceState> = <A extends CatalogAction>(
  partial: Partial<CatalogSliceState> | ((state: TState) => Partial<CatalogSliceState>),
  replace?: boolean,
  action?: A,
) => void;

const INITIAL_STATE: CatalogSliceState = {
  uxonly: {
    catalog: { trafficRules: { maxSpeeds: [], vehicleModes: [] }, fileReferences: [] },
    preferences: {},
    thumbnail: '',
    groups: [],
  },
};

export const createCatalogSlice = <TState extends CatalogSliceState>(
  set: CatalogSet<TState>,
  get: () => TState,
): CatalogActions => ({
  ...INITIAL_STATE,

  getFileReferences() {
    const {
      uxonly: {
        catalog: { fileReferences },
      },
    } = get();
    return Array.isArray(fileReferences) ? fileReferences : [];
  },

  addFileReference(fileReference) {
    set(
      (state) => {
        const {
          uxonly: {
            catalog: { fileReferences: existingFileReferences },
          },
        } = state;
        const normalizedFileReferences = Array.isArray(existingFileReferences)
          ? existingFileReferences
          : [];

        return {
          uxonly: {
            ...state.uxonly,
            catalog: {
              ...state.uxonly.catalog,
              fileReferences: [...normalizedFileReferences, fileReference],
            },
          },
        };
      },
      undefined,
      { type: 'addFileReference' },
    );
  },

  removeFileReference(fileReference) {
    set(
      (state) => {
        const {
          uxonly: {
            catalog: { fileReferences: existingFileReferences },
          },
        } = state;
        const normalizedFileReferences = Array.isArray(existingFileReferences)
          ? existingFileReferences
          : [];

        return {
          uxonly: {
            ...state.uxonly,
            catalog: {
              ...state.uxonly.catalog,
              fileReferences: normalizedFileReferences.filter((fr) => fr.url !== fileReference.url),
            },
          },
        };
      },
      undefined,
      { type: 'removeFileReference' },
    );
  },

  trafficRules: {
    set(trafficRules) {
      set(
        (state) => ({
          uxonly: {
            ...state.uxonly,
            catalog: {
              ...state.uxonly.catalog,
              trafficRules,
            },
          },
        }),
        undefined,
        {
          type: 'catalog/trafficRules/set',
        },
      );
    },

    vehicleModes: {
      set(vehicleModes) {
        set(
          (state) => ({
            uxonly: {
              ...state.uxonly,
              catalog: {
                ...state.uxonly.catalog,
                trafficRules: {
                  ...state.uxonly.catalog.trafficRules,
                  vehicleModes,
                },
              },
            },
          }),
          undefined,
          {
            type: 'catalog/trafficRules/vehicleModes/set',
          },
        );
      },
    },

    maxSpeeds: {
      set(maxSpeeds) {
        set(
          (state) => ({
            uxonly: {
              ...state.uxonly,
              catalog: {
                ...state.uxonly.catalog,
                trafficRules: {
                  ...state.uxonly.catalog.trafficRules,
                  maxSpeeds,
                },
              },
            },
          }),
          undefined,
          {
            type: 'catalog/trafficRules/maxSpeeds/set',
          },
        );
      },
    },
  },
});
